import { useState, useEffect } from 'react';
import { Client, clientService } from '@/services/clientService';
import { supabase } from '@/integrations/supabase/client';

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Get user from supabase directly to avoid Router dependency issues
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id ?? null);
    };
    
    getUser();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  const loadClients = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      console.log('Loading clients for user:', userId);
      const fetchedClients = await clientService.getUserClients();
      console.log('Fetched clients:', fetchedClients);
      setClients(fetchedClients);
    } catch (err) {
      console.error('Error loading clients:', err);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadClients();
    } else {
      setLoading(false);
    }
  }, [userId]);

  // Set up real-time subscription for clients with debouncing
  useEffect(() => {
    if (!userId) return;

    console.log('Setting up real-time subscription for user clients');
    
    let timeoutId: NodeJS.Timeout;
    
    const channel = supabase
      .channel('user_clients')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'clients',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('Clients table change detected:', payload);
          // Debounce rapid changes to prevent excessive API calls
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            loadClients();
          }, 500); // 500ms debounce
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up clients subscription');
      clearTimeout(timeoutId);
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const createClient = async (clientData: any) => {
    try {
      console.log('Creating client:', clientData);
      const newClient = await clientService.createClient(clientData);
      console.log('Client created successfully:', newClient);
      setClients(prev => [newClient, ...prev]);
      return newClient;
    } catch (err) {
      console.error('Error creating client:', err);
      throw err;
    }
  };

  const updateClient = async (clientId: string, updates: any) => {
    try {
      console.log('Updating client:', clientId, updates);
      const updatedClient = await clientService.updateClient(clientId, updates);
      setClients(prev => prev.map(client => 
        client.id === clientId ? updatedClient : client
      ));
      return updatedClient;
    } catch (err) {
      console.error('Error updating client:', err);
      throw err;
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      console.log('Deleting client:', clientId);
      await clientService.deleteClient(clientId);
      setClients(prev => prev.filter(client => client.id !== clientId));
    } catch (err) {
      console.error('Error deleting client:', err);
      throw err;
    }
  };

  return {
    clients,
    loading,
    error,
    loadClients,
    createClient,
    updateClient,
    deleteClient,
  };
};